// The three things a screen shows instead of its data: nothing yet, not yet,
// and something went wrong. DESIGN.md "Szövegírás": an error says what
// happened and what can be done — no apology, no fog.

import type { ComponentType, ReactNode } from "react";
import { CircleCheckIcon, InboxIcon, InfoIcon, OctagonAlertIcon, TriangleAlertIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// --- Empty -----------------------------------------------------------------

interface EmptyProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  /** Inside a card or a table: no border of its own. */
  bare?: boolean;
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = InboxIcon,
  bare = false,
  className,
}: EmptyProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex flex-col items-center gap-2 px-6 py-10 text-center",
        !bare && "rounded-card border border-dashed border-line-strong",
        className,
      )}
    >
      <span className="mb-1 flex size-9 items-center justify-center rounded-inner bg-fill-secondary text-muted-foreground">
        <Icon className="size-4.5" />
      </span>
      <p className="font-semibold text-foreground">{title}</p>
      {description && <div className="max-w-prose text-muted-foreground">{description}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// --- Loading ---------------------------------------------------------------

interface LoadingProps {
  /** Said to screen readers, and shown when `lines` is 0. */
  label?: string;
  /** Placeholder lines; 0 shows the label instead. */
  lines?: number;
  className?: string;
}

/** A still skeleton — DESIGN.md allows no pulsing. */
export function LoadingState({ label = "Betöltés…", lines = 3, className }: LoadingProps) {
  if (lines === 0) {
    return (
      <p role="status" className={cn("text-muted-foreground", className)}>
        {label}
      </p>
    );
  }
  return (
    <div role="status" aria-label={label} className={cn("flex flex-col gap-3", className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn("h-4", index % 3 === 2 ? "w-1/2" : "w-full")} />
      ))}
    </div>
  );
}

// --- Error -----------------------------------------------------------------

interface Described {
  what: string;
  todo: string;
}

/** The API's own messages already say what happened, in Hungarian. The
 * browser's and the proxy's do not; those get a sentence that does. */
export function describeError(message: string): Described {
  const text = message.trim();
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(text)) {
    return {
      what: "Az API nem érhető el.",
      todo: "Ellenőrizd, hogy fut-e (helyben: python -m emc_worker.dev up), aztán töltsd újra az oldalt.",
    };
  }
  const status = /^HTTP (\d{3})$/.exec(text);
  if (status) {
    const code = Number(status[1]);
    if (code === 401) return { what: "A belépés lejárt.", todo: "Lépj be újra." };
    if (code === 403) {
      return { what: "Ehhez nincs jogosultságod.", todo: "Kérd meg egy admint, vagy kérj admin szerepet." };
    }
    if (code === 404) return { what: "Nincs ilyen elem.", todo: "Lehet, hogy közben törölték; menj vissza a listához." };
    if (code >= 500) {
      return {
        what: `Az API hibával válaszolt (HTTP ${code}).`,
        todo: "Próbáld újra egy perc múlva; ha marad, az API naplója mondja meg, mi történt.",
      };
    }
    return { what: `Az API elutasította a kérést (HTTP ${code}).`, todo: "Ellenőrizd a megadott adatokat." };
  }
  return { what: text === "" ? "Ismeretlen hiba." : text, todo: "" };
}

interface ErrorProps {
  /** The message the page caught — shown as it is when it already says what happened. */
  message: string;
  title?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({ message, title, onRetry, className }: ErrorProps) {
  const described = describeError(message);
  return (
    <div
      role="alert"
      data-slot="error-state"
      className={cn(
        "flex items-start gap-3 rounded-inner bg-red-soft px-4 py-3 text-foreground",
        className,
      )}
    >
      <OctagonAlertIcon className="mt-0.5 size-4.5 shrink-0 text-destructive" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title && <p className="font-semibold text-red-ink">{title}</p>}
        <p className={cn("break-words", !title && "font-semibold text-red-ink")}>{described.what}</p>
        {described.todo && <p className="text-muted-foreground">{described.todo}</p>}
      </div>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          Újra
        </Button>
      )}
    </div>
  );
}

// --- Callout: a sentence the page wants seen --------------------------------

// The tinted panels of the theme: gray for a fact, green for done, orange for
// "look at this". The icon carries the hue; the text stays the body colour.
const CALLOUT = {
  info: { box: "bg-fill-secondary", icon: InfoIcon, iconClass: "text-muted-foreground" },
  success: { box: "bg-green-soft", icon: CircleCheckIcon, iconClass: "text-success" },
  warn: { box: "bg-orange-soft", icon: TriangleAlertIcon, iconClass: "text-warning" },
} as const;

interface CalloutProps {
  tone?: keyof typeof CALLOUT;
  children: ReactNode;
  className?: string;
}

/** A notice that is not an error: "A sablon elkészült.", "nem szerkeszthető". */
export function Callout({ tone = "info", children, className }: CalloutProps) {
  const look = CALLOUT[tone];
  const Icon = look.icon;
  return (
    <div
      role="status"
      data-slot="callout"
      className={cn("flex items-start gap-2.5 rounded-inner px-3.5 py-3 text-detail text-foreground", look.box, className)}
    >
      <Icon className={cn("mt-px size-4.5 shrink-0", look.iconClass)} />
      <div className="min-w-0 flex-1 break-words">{children}</div>
    </div>
  );
}
